/**
 * מאזן — the service agreement, signed on the summary step BEFORE payment
 * (the server refuses checkout without it).
 *
 * The customer's details are prefilled from the account; whatever is missing
 * is left empty, highlighted in the contract text, and asked for in the form.
 * The contract text below fills in live as they type. Signing = details +
 * the "קראתי והבנתי" box + a drawn signature. The server renders the signed
 * PDF and emails it to the customer and the office.
 */

import { useCallback, useEffect, useState } from 'react'
import { CheckCircle2, Download, FileSignature } from 'lucide-react'
import SignaturePad from '../../../components/signature/SignaturePad'
import { handleApiError } from '../../../services/api'
import { CheckField, TextField } from './fields'
import { isValidIsraeliId, mazanApi, PHONE_RE, type ContractInfo, type ContractSigner } from '../model'

const ERROR_ID = 'mz-contract-error'

type Field = 'fullName' | 'idNumber' | 'address' | 'phone'
const FIELD_LABEL: Record<Field, string> = { fullName: 'שם מלא', idNumber: 'ת.ז.', address: 'כתובת', phone: 'טלפון' }

interface Props {
  reportId: string
  /** called with true once the agreement is signed (now or earlier) */
  onSigned: (signed: boolean) => void
}

export default function ContractSection({ reportId, onSigned }: Props) {
  const [info, setInfo] = useState<ContractInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [signer, setSigner] = useState<ContractSigner | null>(null)
  /** fields that were empty in the account — shown as "להשלמה" */
  const [missing, setMissing] = useState<Field[]>([])
  const [approved, setApproved] = useState(false)
  const [signature, setSignature] = useState('')
  const [sigEmpty, setSigEmpty] = useState(true)
  const [error, setError] = useState<{ message: string; field?: string } | null>(null)
  const [working, setWorking] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(() => {
    mazanApi.contract(reportId)
      .then((c) => {
        setInfo(c)
        setSigner(c.signer)
        setMissing((['fullName', 'idNumber', 'address', 'phone'] as Field[]).filter((f) => !c.signer[f].trim()))
        onSigned(!!c.signed)
      })
      .catch((e) => setLoadError(handleApiError(e)))
  }, [reportId, onSigned])
  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (error?.field) document.getElementById(error.field)?.focus()
  }, [error])

  if (loadError) return <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">{loadError}</p>
  if (!info || !signer) return <p role="status" className="text-gray-700">טוען את ההסכם…</p>

  const set = (f: Field) => (v: string) => {
    setError(null)
    setSigner({ ...signer, [f]: f === 'idNumber' ? v.replace(/\D/g, '').slice(0, 9) : v })
  }

  const validate = (): { message: string; field?: string } | null => {
    if (signer.fullName.trim().length < 2) return { message: 'יש להזין שם מלא', field: 'mz-c-fullName' }
    if (!isValidIsraeliId(signer.idNumber.trim())) return { message: 'מספר ת.ז. אינו תקין (9 ספרות כולל ספרת ביקורת)', field: 'mz-c-idNumber' }
    if (signer.address.trim().length < 3) return { message: 'יש להזין כתובת מלאה', field: 'mz-c-address' }
    if (!PHONE_RE.test(signer.phone.trim())) return { message: 'מספר טלפון לא תקין', field: 'mz-c-phone' }
    if (!approved) return { message: 'יש לסמן שקראת והבנת את ההסכם', field: 'mz-c-approve' }
    if (sigEmpty || !signature) return { message: 'יש לחתום בתיבת החתימה', field: 'mz-c-signature' }
    return null
  }

  const sign = async () => {
    const v = validate()
    if (v) { setError(v); return }
    setWorking(true)
    setError(null)
    try {
      await mazanApi.signContract(reportId, {
        version: info.version,
        fullName: signer.fullName.trim(),
        idNumber: signer.idNumber.trim(),
        address: signer.address.trim(),
        phone: signer.phone.trim(),
        signatureImage: signature,
        approved: true,
      })
      load()
    } catch (e) {
      setError({ message: handleApiError(e) })
    } finally {
      setWorking(false)
    }
  }

  const download = async () => {
    setDownloading(true)
    try { await mazanApi.downloadContract(reportId) } catch (e) { setError({ message: handleApiError(e) }) } finally { setDownloading(false) }
  }

  // ── already signed ──
  if (info.signed) {
    return (
      <section className="card border-t-4 border-emerald-800" aria-labelledby="mz-contract">
        <h3 id="mz-contract" className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <CheckCircle2 size={20} className="text-emerald-800" aria-hidden="true" /> ההסכם נחתם
        </h3>
        <p className="mt-2 text-sm text-gray-800">
          {info.title} נחתם ביום {info.today}. עותק חתום נשלח לכתובת <span dir="ltr">{info.signer.email}</span>.
        </p>
        {error && <p role="alert" className="mt-2 text-sm text-red-800">{error.message}</p>}
        <button type="button" className="btn btn-secondary mt-3 inline-flex items-center gap-2" onClick={download} disabled={downloading}>
          <Download size={16} aria-hidden="true" /> {downloading ? 'מוריד…' : 'הורדת ההסכם החתום (PDF)'}
        </button>
      </section>
    )
  }

  // ── to sign ──
  const P = info.provider
  const fill = (f: Field | 'email') => {
    const v = signer[f].trim()
    return v
      ? <span className="mx-1 border-b border-gray-700 px-1 font-semibold text-gray-900">{v}</span>
      : <mark className="mx-1 rounded bg-amber-100 px-1 text-amber-950">[להשלמה: {f === 'email' ? 'דוא"ל' : FIELD_LABEL[f]}]</mark>
  }
  const isInvalid = (id: string) => error?.field === id

  return (
    <section className="card border-t-4 border-[#1c355e]" aria-labelledby="mz-contract">
      <h3 id="mz-contract" className="flex items-center gap-2 text-base font-semibold text-gray-900">
        <FileSignature size={20} aria-hidden="true" /> חתימה על הסכם השירות
      </h3>
      <p className="mt-1 text-sm text-gray-700">
        לפני התשלום יש לקרוא ולחתום על ההסכם. הפרטים שכבר קיימים בחשבון מולאו אוטומטית
        {missing.length > 0 ? <> — יש להשלים: <strong>{missing.map((f) => FIELD_LABEL[f]).join(', ')}</strong></> : ''}.
        עותק חתום יישלח אליך למייל.
      </p>

      <fieldset className="mt-4">
        <legend className="mb-2 text-sm font-semibold text-gray-900">פרטי הלקוח בהסכם</legend>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField id="mz-c-fullName" label="שם מלא" value={signer.fullName} autoComplete="name"
            invalidId={error?.field} errorId={ERROR_ID} onChange={set('fullName')} />
          <TextField id="mz-c-idNumber" label="מספר ת.ז." value={signer.idNumber} dir="ltr" inputMode="numeric"
            invalidId={error?.field} errorId={ERROR_ID} onChange={set('idNumber')} />
          <TextField id="mz-c-address" label="כתובת" className="sm:col-span-2" value={signer.address} autoComplete="street-address"
            placeholder="רחוב ומספר, עיר" invalidId={error?.field} errorId={ERROR_ID} onChange={set('address')} />
          <TextField id="mz-c-phone" label="טלפון" value={signer.phone} dir="ltr" inputMode="text" autoComplete="tel"
            invalidId={error?.field} errorId={ERROR_ID} onChange={set('phone')} />
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-800">דוא"ל</span>
            <p className="input bg-gray-50 text-gray-700" dir="ltr">{signer.email}</p>
            <p className="mt-1 text-xs text-gray-600">כתובת החשבון — אליה יישלחו ההסכם והדוח.</p>
          </div>
        </div>
      </fieldset>

      {/* The agreement itself, filled live. A scrollable region must be focusable to scroll by keyboard. */}
      <div
        className="mt-5 max-h-[28rem] overflow-y-auto rounded-lg border border-gray-300 bg-white p-4 text-sm leading-7 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        role="region"
        aria-label={`נוסח ההסכם: ${info.title}`}
      >
        <h4 className="bg-[#1c355e] px-3 py-2 text-base font-bold text-white">{info.title}</h4>
        <p className="mt-3 text-center font-semibold">שנערך ונחתם באופן מקוון ביום {info.today}</p>
        <p className="mt-3"><b>בין:</b> {P.name}, ת.ז. {P.idNumber}, מרחוב {P.address}, {P.description} ("נותן השירות")</p>
        <p className="mt-1"><b>לבין:</b> שם:{fill('fullName')} ת.ז.:{fill('idNumber')} כתובת:{fill('address')}</p>
        <p className="mt-1">דוא"ל:<span dir="ltr">{fill('email')}</span> טלפון:<span dir="ltr">{fill('phone')}</span> ("הלקוח")</p>
        <p className="mt-3">{info.preamble.map((r, i) => (r.bold ? <b key={i}>{r.text}</b> : <span key={i}>{r.text}</span>))}</p>
        {info.sections.map((s) => (
          <div key={s.n}>
            <h5 className="mt-4 bg-[#1c355e] px-3 py-1.5 font-bold text-white">{s.n}. {s.title}</h5>
            {s.clauses.map((c) => (
              <div key={c.n} className="mt-2">
                <p><b>{c.n}</b> {c.term && <b>{c.term} </b>}{c.text}</p>
                {c.bullets && <ul className="ms-6 list-disc">{c.bullets.map((b) => <li key={b}>{b}</li>)}</ul>}
              </div>
            ))}
          </div>
        ))}
      </div>

      <h4 className="mt-5 bg-[#1c355e] px-3 py-1.5 text-sm font-bold text-white">אישור הלקוח</h4>
      <div className="mt-3">
        <CheckField id="mz-c-approve" checked={approved} onChange={(v) => { setApproved(v); setError(null) }} label={info.approval} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg bg-slate-100 p-3"><dt className="text-gray-600">שם הלקוח</dt><dd className="font-semibold">{signer.fullName || '—'}</dd></div>
        <div className="rounded-lg bg-slate-100 p-3"><dt className="text-gray-600">תאריך</dt><dd className="font-semibold">{info.today}</dd></div>
      </dl>
      <div className="mt-3" id="mz-c-signature" tabIndex={-1}>
        <p id="mz-c-signature-label" className="mb-1 text-sm font-medium text-gray-800">חתימת הלקוח</p>
        <SignaturePad
          onSignatureChange={(d) => { setSignature(d); setError(null) }}
          isEmpty={sigEmpty}
          setIsEmpty={setSigEmpty}
          ariaLabel="תיבת חתימה בכתב יד — חובה. ציירו את חתימתכם עם העכבר או האצבע"
          describedBy={isInvalid('mz-c-signature') ? ERROR_ID : undefined}
        />
      </div>

      {error && (
        <p id={ERROR_ID} role="alert" className="mt-3 rounded-lg bg-red-50 p-3 text-sm font-medium text-red-800">{error.message}</p>
      )}

      <button type="button" className="btn btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 sm:w-auto" onClick={sign} disabled={working}>
        <FileSignature size={18} aria-hidden="true" /> {working ? 'חותם ומפיק את ההסכם…' : 'חתימה על ההסכם והמשך לתשלום'}
      </button>
    </section>
  )
}
